'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Info, Loader2 } from 'lucide-react'
import { PreTripVideoCard } from '@/features/stm/components/group-detail/PreTripVideoCard'
import {
  shouldUseVideoMultipartUpload,
  uploadBaseVideoMultipartChunks,
} from '@/features/photos/lib/upload-video-multipart'
import { authFetch, authFetchJson } from '@/shared/lib/auth-fetch'
import { UserRole } from '@/shared/constants/user-roles'

export const HUB_ABOUT_VIDEO_SLOTS = [
  { key: 'about-reliefhub', title: 'About ReliefHub' },
  { key: 'volunteer-role', title: 'ReliefHub Volunteer Role' },
  { key: 'team-leader-role', title: 'ReliefHub Team Leader Role' },
  { key: 'group-leader-role', title: 'ReliefHub Group Leader Role' },
  { key: 'partners-role', title: 'ReliefHub Partners Role' },
  { key: 'homeowners-role', title: 'ReliefHub Homeowners Role' },
  { key: 'supporters-role', title: 'ReliefHub Supporters Role' },
  { key: 'base-leader-role', title: 'ReliefHub Base Leader Role' },
] as const

export type HubAboutVideoSlotKey = (typeof HUB_ABOUT_VIDEO_SLOTS)[number]['key']

export type HubAboutVideo = {
  slotKey: HubAboutVideoSlotKey
  videoUrl: string
  videoThumbnailUrl: string | null
  videoDuration: number | null
}

function defaultVisibleSlotKeys(
  role: string | undefined,
  isGroupContact: boolean,
): Set<HubAboutVideoSlotKey> {
  const keys = new Set<HubAboutVideoSlotKey>(['about-reliefhub'])
  if (role === UserRole.VOLUNTEER) {
    keys.add('volunteer-role')
  }
  if (role === UserRole.TEAM_LEADER) {
    keys.add('volunteer-role')
    keys.add('team-leader-role')
    if (isGroupContact) keys.add('group-leader-role')
  }
  if (role === UserRole.BASE_LEADER) {
    keys.add('volunteer-role')
    keys.add('team-leader-role')
    keys.add('base-leader-role')
    if (isGroupContact) keys.add('group-leader-role')
  }
  if (role === UserRole.PARTNER) keys.add('partners-role')
  if (role === UserRole.HOMEOWNER) keys.add('homeowners-role')
  if (role === UserRole.Supporter) keys.add('supporters-role')
  return keys
}

async function fetchHubAboutVideos(): Promise<HubAboutVideo[]> {
  const res = await authFetchJson<HubAboutVideo[]>(`/base-photos/about`)
  if (!res.success) {
    throw new Error(res.message || 'Failed to load Tutorials videos')
  }
  if (!Array.isArray(res.data)) {
    throw new Error('Invalid Tutorials videos response')
  }
  return res.data.filter((row): row is HubAboutVideo =>
    HUB_ABOUT_VIDEO_SLOTS.some((slot) => slot.key === row.slotKey),
  )
}

type HubAboutTabContentProps = {
  canEditAbout: boolean
  allowHardDeleteAboutVideo?: boolean
  userRole?: string
}

export function HubAboutTabContent({
  canEditAbout,
  allowHardDeleteAboutVideo = false,
  userRole,
}: HubAboutTabContentProps) {
  const queryClient = useQueryClient()
  const [uploadingVideoSlot, setUploadingVideoSlot] = useState<HubAboutVideoSlotKey | null>(null)
  const [uploadingPosterSlot, setUploadingPosterSlot] = useState<HubAboutVideoSlotKey | null>(null)
  const [revertingPosterSlot, setRevertingPosterSlot] = useState<HubAboutVideoSlotKey | null>(null)
  const [deletingVideoSlot, setDeletingVideoSlot] = useState<HubAboutVideoSlotKey | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const queryKey = useMemo(() => ['hub-about-videos'] as const, [])
  const { data: videos = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: fetchHubAboutVideos,
  })

  const { data: isGroupContact = false } = useQuery({
    queryKey: ['is-group-contact'],
    queryFn: async () => {
      const res = await authFetchJson<{ isGroupContact?: boolean }>('/volunteer/is-group-contact')
      return res.success && res.data?.isGroupContact === true
    },
    enabled: userRole === UserRole.TEAM_LEADER || userRole === UserRole.BASE_LEADER,
  })

  const videosBySlot = useMemo(() => {
    const map = new Map<HubAboutVideoSlotKey, HubAboutVideo>()
    videos.forEach((video) => map.set(video.slotKey, video))
    return map
  }, [videos])

  const defaultKeys = useMemo(
    () => defaultVisibleSlotKeys(userRole, isGroupContact),
    [userRole, isGroupContact],
  )

  const visibleSlots = useMemo(() => {
    if (canEditAbout) return HUB_ABOUT_VIDEO_SLOTS
    const withVideo = HUB_ABOUT_VIDEO_SLOTS.filter((slot) => videosBySlot.has(slot.key))
    if (showAll) return withVideo
    return withVideo.filter((slot) => defaultKeys.has(slot.key))
  }, [canEditAbout, videosBySlot, showAll, defaultKeys])

  const hasExtraVideos =
    !canEditAbout &&
    HUB_ABOUT_VIDEO_SLOTS.some(
      (slot) => videosBySlot.has(slot.key) && !defaultKeys.has(slot.key),
    )

  const refetchVideos = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey })
  }, [queryClient, queryKey])

  const handleVideoUpload = useCallback(
    async (
      slotKey: HubAboutVideoSlotKey,
      file: File,
      options?: { keepCustomPoster?: boolean }
    ) => {
      const videoTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v']
      if (!videoTypes.includes(file.type) && !/\.(mp4|mov|webm|m4v)$/i.test(file.name)) {
        setUploadError('Please choose a video file (MP4, MOV, WebM, or M4V).')
        return
      }
      const maxMb = 500
      if (file.size > maxMb * 1024 * 1024) {
        setUploadError(`Video must be under ${maxMb} MB.`)
        return
      }

      setUploadError(null)
      setUploadingVideoSlot(slotKey)
      try {
        if (shouldUseVideoMultipartUpload(file)) {
          await uploadBaseVideoMultipartChunks(
            null,
            file,
            {
              baseFinalizeKind: 'hub_about',
              hubAboutSlotKey: slotKey,
              keepCustomPoster: options?.keepCustomPoster,
            },
            undefined
          )
          await refetchVideos()
          return
        }

        const formData = new FormData()
        formData.append('slotKey', slotKey)
        if (options?.keepCustomPoster) {
          formData.append('keepCustomPoster', 'true')
        }
        formData.append('video', file)
        const res = await authFetch('/base-photos/about/video', {
          method: 'POST',
          body: formData,
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || !json.success) {
          setUploadError(json.message || json.error || res.statusText || 'Upload failed')
          return
        }
        await refetchVideos()
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : 'Failed to upload video.')
      } finally {
        setUploadingVideoSlot(null)
      }
    },
    [refetchVideos]
  )

  const handlePosterUpload = useCallback(
    async (slotKey: HubAboutVideoSlotKey, file: File) => {
      if (!file.type.startsWith('image/') && !/\.(jpe?g|png|gif|webp|bmp|tiff?|heic|heif|avif)$/i.test(file.name)) {
        setUploadError('Please choose an image file.')
        return
      }

      setUploadError(null)
      setUploadingPosterSlot(slotKey)
      try {
        const formData = new FormData()
        formData.append('slotKey', slotKey)
        formData.append('poster', file)
        const res = await authFetch('/base-photos/about/poster', {
          method: 'POST',
          body: formData,
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || !json.success) {
          setUploadError(json.message || json.error || res.statusText || 'Upload failed')
          return
        }
        await refetchVideos()
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : 'Failed to upload poster.')
      } finally {
        setUploadingPosterSlot(null)
      }
    },
    [refetchVideos]
  )

  const handleHardDeleteVideo = useCallback(
    async (slotKey: HubAboutVideoSlotKey) => {
      setUploadError(null)
      setDeletingVideoSlot(slotKey)
      try {
        const res = await authFetch('/base-photos/about/video/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slotKey }),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || !json.success) {
          const msg =
            json.message || json.error || res.statusText || 'Delete failed'
          setUploadError(msg)
          throw new Error(msg)
        }
        await refetchVideos()
      } finally {
        setDeletingVideoSlot(null)
      }
    },
    [refetchVideos]
  )

  const handleRevertPoster = useCallback(
    async (slotKey: HubAboutVideoSlotKey) => {
      setUploadError(null)
      setRevertingPosterSlot(slotKey)
      try {
        const res = await authFetch('/base-photos/about/poster/revert', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slotKey }),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || !json.success) {
          setUploadError(json.message || json.error || res.statusText || 'Revert failed')
          return
        }
        await refetchVideos()
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : 'Failed to reset poster.')
      } finally {
        setRevertingPosterSlot(null)
      }
    },
    [refetchVideos]
  )

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading Tutorials...
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardContent className="flex items-center gap-2 py-6 text-sm text-destructive">
          <Info className="h-5 w-5" />
          {error instanceof Error ? error.message : 'Unable to load Tutorials videos.'}
        </CardContent>
      </Card>
    )
  }

  if (visibleSlots.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Tutorial videos will appear here when they are available.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {uploadError && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="py-4 text-sm text-destructive">
            {uploadError}
          </CardContent>
        </Card>
      )}
      {visibleSlots.map((slot) => {
        const video = videosBySlot.get(slot.key)
        return (
          <PreTripVideoCard
            key={slot.key}
            title={slot.title}
            url=""
            videoUrl={video?.videoUrl ?? null}
            videoThumbnailUrl={video?.videoThumbnailUrl ?? null}
            videoDuration={video?.videoDuration ?? null}
            onUploadVideo={
              canEditAbout
                ? (file, options) => handleVideoUpload(slot.key, file, options)
                : undefined
            }
            isUploadingVideo={uploadingVideoSlot === slot.key}
            onUploadPoster={
              canEditAbout && video
                ? (file) => handlePosterUpload(slot.key, file)
                : undefined
            }
            isUploadingPoster={uploadingPosterSlot === slot.key}
            onRevertPoster={
              canEditAbout && video
                ? () => handleRevertPoster(slot.key)
                : undefined
            }
            isRevertingPoster={revertingPosterSlot === slot.key}
            onDeleteVideo={
              allowHardDeleteAboutVideo && video
                ? () => handleHardDeleteVideo(slot.key)
                : undefined
            }
            isDeletingVideo={deletingVideoSlot === slot.key}
            replaceVideoDialogTitle={`Replace ${slot.title} video?`}
            replaceVideoDialogDescription="Choose what happens to the poster image shown before play. You can also change the poster anytime after upload."
          />
        )
      })}
      {hasExtraVideos && (
        <div className="flex justify-center">
          <Button type="button" variant="outline" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show my tutorials' : 'Show all tutorials'}
          </Button>
        </div>
      )}
    </div>
  )
}
